/**
 * Validadores para CPF, placa e outros campos.
 */

/**
 * Valida um CPF (algoritmo oficial dos dígitos verificadores).
 */
export function validarCPF(cpf: string): boolean {
    if (!cpf) return false
    const cleaned = cpf.replace(/\D/g, '')

    if (cleaned.length !== 11) return false

    // Valida primeiro dígito verificador
    let sum = 0
    for (let i = 0; i < 9; i++) {
        sum += parseInt(cleaned.charAt(i)) * (10 - i)
    }
    let remainder = (sum * 10) % 11
    if (remainder === 10) remainder = 0
    if (remainder !== parseInt(cleaned.charAt(9))) return false

    // Valida segundo dígito verificador
    sum = 0
    for (let i = 0; i < 10; i++) {
        sum += parseInt(cleaned.charAt(i)) * (11 - i)
    }
    remainder = (sum * 10) % 11
    if (remainder === 10) remainder = 0
    if (remainder !== parseInt(cleaned.charAt(10))) return false

    return true
}

/**
 * Formata CPF: 000.000.000-00
 */
export function formatarCPF(cpf: string): string {
    if (!cpf) return ''
    const cleaned = cpf.replace(/\D/g, '')
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}

/**
 * Mascara CPF para exibição: ***.***.***-XX (mostra apenas últimos 2 dígitos)
 */
export function mascararCPF(cpf: string): string {
    if (!cpf) return '***.***.***-**'
    const cleaned = cpf.replace(/\D/g, '')
    if (cleaned.length < 11) return cpf
    return `***.***.*${cleaned.charAt(8)}${cleaned.charAt(9)}-${cleaned.slice(9)}`
}

/**
 * Valida placa (formato antigo AAA-0000 ou Mercosul AAA0A00)
 */
export function validarPlaca(placa: string): boolean {
    if (!placa) return false
    const cleaned = placa.replace(/[-\s]/g, '').toUpperCase()
    // Formato antigo: AAA0000
    const oldFormat = /^[A-Z]{3}\d{4}$/
    // Formato Mercosul: AAA0A00
    const mercosulFormat = /^[A-Z]{3}\d[A-Z]\d{2}$/
    return oldFormat.test(cleaned) || mercosulFormat.test(cleaned)
}

/**
 * Formata placa para exibição: AAA-0000 ou AAA0A00
 */
export function formatarPlaca(placa: string): string {
    if (!placa) return ''
    const cleaned = placa.replace(/[-\s]/g, '').toUpperCase()
    // Formato antigo
    if (/^[A-Z]{3}\d{4}$/.test(cleaned)) {
        return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`
    }
    return cleaned
}

/**
 * Valida CNPJ
 */
export function validarCNPJ(cnpj: string): boolean {
    if (!cnpj) return false
    const cleaned = cnpj.replace(/\D/g, '')
    if (cleaned.length !== 14) return false
    if (/^(\d)\1{13}$/.test(cleaned)) return false

    const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
    const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]

    let sum = 0
    for (let i = 0; i < 12; i++) {
        sum += parseInt(cleaned.charAt(i)) * weights1[i]!
    }
    let remainder = sum % 11
    const digit1 = remainder < 2 ? 0 : 11 - remainder

    if (parseInt(cleaned.charAt(12)) !== digit1) return false

    sum = 0
    for (let i = 0; i < 13; i++) {
        sum += parseInt(cleaned.charAt(i)) * weights2[i]!
    }
    remainder = sum % 11
    const digit2 = remainder < 2 ? 0 : 11 - remainder

    return parseInt(cleaned.charAt(13)) === digit2
}

/**
 * Valida e-mail (regex simples)
 */
export function validarEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

/**
 * Valida telefone brasileiro (10-11 dígitos)
 */
export function validarTelefone(telefone: string): boolean {
    if (!telefone) return false
    const cleaned = telefone.replace(/\D/g, '')
    return cleaned.length >= 10 && cleaned.length <= 11
}

/**
 * Formata telefone: (00) 00000-0000
 */
export function formatarTelefone(telefone: string): string {
    if (!telefone) return ''
    const cleaned = telefone.replace(/\D/g, '')
    if (cleaned.length === 11) {
        return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
    }
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
}
